import { useEffect, useState } from "react";

export type ScriptStatuses = "loading" | "error" | "ready" | "unknown";

type UseScript = ({
  scriptSrc,
  attributes,
  removeOnUnmount,
  isEnabled,
}: {
  scriptSrc: string;
  attributes?: Partial<ScriptAttributes>;
  removeOnUnmount?: boolean;
  isEnabled?: boolean;
}) => {
  scriptStatus: ScriptStatuses;
};

const SCRIPT_ATTRIBUTE_KEYS = ["integrity", "crossOrigin", "id"] as const;
export type ScriptAttributes = Pick<HTMLScriptElement, (typeof SCRIPT_ATTRIBUTE_KEYS)[number]>;

const isScriptAttribute = (key: string): key is keyof ScriptAttributes => {
  return SCRIPT_ATTRIBUTE_KEYS.includes(key as keyof ScriptAttributes);
};

export const useScript: UseScript = ({ scriptSrc, attributes, removeOnUnmount, isEnabled = true }) => {
  const [scriptStatus, setScriptStatus] = useState<ScriptStatuses>("unknown");

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    let scriptNode = document.querySelector(`script[src="${scriptSrc}"]`) as HTMLScriptElement | null;
    const domStatus = scriptNode?.getAttribute("data-status");

    if (domStatus === "ready" || domStatus === "error") {
      setScriptStatus(domStatus);
      return;
    }

    if (!scriptNode) {
      scriptNode = document.createElement("script");
      scriptNode.src = scriptSrc;
      scriptNode.async = true;
      scriptNode.setAttribute("data-status", "loading");

      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          if (isScriptAttribute(key) && value !== null && value !== undefined) {
            scriptNode![key] = value;
          }
        });
      }

      const setDomReady = () => scriptNode?.setAttribute("data-status", "ready");
      const setDomError = () => scriptNode?.setAttribute("data-status", "error");

      scriptNode.addEventListener("load", setDomReady);
      scriptNode.addEventListener("error", setDomError);

      document.body.appendChild(scriptNode);
    } else {
      if (!domStatus) {
        scriptNode.setAttribute("data-status", "loading");
      }
    }

    setScriptStatus("loading");

    const handleScriptLoad = () => setScriptStatus("ready");
    const handleScriptError = () => setScriptStatus("error");

    scriptNode.addEventListener("load", handleScriptLoad);
    scriptNode.addEventListener("error", handleScriptError);

    return () => {
      if (removeOnUnmount) {
        scriptNode?.remove();
      } else {
        scriptNode?.removeEventListener("load", handleScriptLoad);
        scriptNode?.removeEventListener("error", handleScriptError);
      }
    };
  }, [scriptSrc, attributes, removeOnUnmount, isEnabled]);

  return { scriptStatus };
};
